/**
 * The battle pass (ACT HQ t-931, DR-0021, platform/battle-pass/BATTLE-PASS.md): what the
 * home server's /pass routes return, and the level maths both sides share. Monthly passes
 * of 50 levels; XP comes only from the game servers (migration pass_p1).
 */

/** A game that pays pass XP. */
export type PassGame = "firestrike" | "groundwar" | "vectordrift" | "ludus";

/** The column a track item sits in on the pass screen. */
export type PassTrackGame = "shooters" | "ludus" | "vectordrift" | "hub" | "account";

/** 'chest' items come in the level's chest; 'card' items (calling cards) on reaching the level. */
export type PassSlot = "chest" | "card";

export type PassTrackItem = { level: number; itemId: string; slot: PassSlot; game: PassTrackGame };

export type PassSeason = {
  id: string;
  name: string;
  startsAt: string;
  endsAt: string;
  /** The XP each of the 50 levels costs. */
  levelXp: number[];
  /** The live multiplier (a double-XP weekend is 2). */
  xpMultiplier: number;
  /** False before it opens: the screen says when it does. */
  open: boolean;
  /** Whether chests can be opened. Locked until Theo finalises the contents (14 Oct 2026);
   *  XP and levels count either way. Missing from an older server: treat as locked. */
  chestsOpen?: boolean;
};

export type PassProgress = { run: number; xp: number; level: number };
export type PassClaim = { level: number; opened: boolean };
export type PassAwardLine = { label: string; xp: number };
export type PassRecentAward = { game: PassGame; xp: number; at: string; lines: PassAwardLine[] };

/** GET /pass/me */
export type PassMeResponse = {
  season: PassSeason | null;
  progress: PassProgress;
  track: PassTrackItem[];
  claims: PassClaim[];
  recent: PassRecentAward[];
};

/** POST /pass/open { level } */
export type PassOpenResponse = {
  season: string;
  level: number;
  items: Array<{ itemId: string; slot: PassSlot; game: PassTrackGame }>;
  /** True when this chest was opened before (the same items again, no new copies). */
  repeat: boolean;
};

export type PassErrorCode =
  | "unauthenticated"
  | "invalid_request"
  | "unavailable"
  | "no_season"
  | "sign_in_first"
  | "not_reached"
  | "unknown_account"
  | "locked";

/** Ids of things the pass gives that aren't catalogue items (1 AUC every 5 levels). */
export const PASS_CURRENCY_PREFIX = "currency.";

/** Where an amount of XP sits on a season's curve: the level reached (0–50), the XP into
 *  the next level, and what that level costs (null at 50). Mirrors pass_level_for. */
export function passLevelOf(levelXp: readonly number[], xp: number): { level: number; into: number; next: number | null } {
  let spent = 0;
  for (let i = 0; i < levelXp.length; i++) {
    const cost = levelXp[i] ?? 0;
    if (xp < spent + cost) return { level: i, into: xp - spent, next: cost };
    spent += cost;
  }
  return { level: levelXp.length, into: xp - spent, next: null };
}

/** What a game room sends a player's client when their pass award lands (the post-match XP
 *  bar, ACT HQ t-935): `room.send(client, PASS_AWARD_MESSAGE, message)`. */
export const PASS_AWARD_MESSAGE = "pass.award";

export type PassAwardMessage = {
  game: PassGame;
  xpGained: number;
  xpAfter: number;
  levelBefore: number;
  levelAfter: number;
  dailyBonus: number;
  /** The game's own lines ("12 waves cleared"), as it sent them to the award. */
  lines: PassAwardLine[];
  /** The season's level costs, for the fill to place the XP on the bar (build the message
   *  with the server's passAwardMessage). Missing from older servers: the fill hides the bar. */
  levelXp?: number[];
};

/** GET /pass/records: the player's own stats from each game (Theo, 14 Oct 2026: a "records"
 *  tab "where you can see stats from each game"). A game's entry is null when they've no
 *  record there. */
export type PassRecordsResponse = {
  pass: { season: string | null; level: number; xp: number; xpByGame: Partial<Record<PassGame, number>> };
  firestrike: { runs: number; kills: number; deaths: number; headshots: number; bestScore: number; bestWave: number } | null;
  groundwar: {
    matches: number;
    wins: number;
    losses: number;
    kills: number;
    deaths: number;
    assists: number;
    headshots: number;
    captures: number;
    mvps: number;
    bestStreak: number;
    score: number;
    shotsFired: number;
    shotsHit: number;
    secondsPlayed: number;
  } | null;
  ludus: Array<{ mode: string; rating: number; best: number; games: number; wins: number; top4: number }>;
  vectordrift: { races: number; finished: number; laps: number; bestLaps: Array<{ course: string; bestLapMs: number }> } | null;
};

/** One row of a board. */
export type PassBoardRow = { rank: number; name: string; value: number; you: boolean };

/** A leaderboard: its top, and where the player stands on it (Theo, 14 Oct 2026: a
 *  "leaderboard" tab "which shows your stats vs the global leaderboard"). `lowerIsBetter`
 *  for times. */
export type PassBoard = {
  id: string;
  title: string;
  unit: "xp" | "score" | "rating" | "ms";
  lowerIsBetter: boolean;
  players: number;
  top: PassBoardRow[];
  you: PassBoardRow | null;
};

/** GET /pass/leaderboard */
export type PassLeaderboardResponse = { boards: PassBoard[] };
